<!--
// app/pages/[region]/stats/teams/index.vue
-->
<script setup lang="ts">
import HeaderBannerTitle from "~/app/components/Shared/HeaderBannerTitle.vue";
import SeasonsFilter from "~/app/components/Shared/SeasonsFilter.vue";
import SidebarMenuStats from "~/app/components/pages/region-stats/SidebarMenuStats.vue";
import GridFeaturedStatsTeamsOverview from "~/app/components/pages/region-stats/GridFeaturedStatsTeamsOverview.vue";
import { useSeasonFilter } from "~/app/composables/season/seasonFilter";
import { useWebContext } from "~/app/composables/webContext";
import { useCompetitionStore } from "~/app/stores/competitionStore";
import { $SEASONS_WITHOUT_DATA } from "~/src/season-cluster/infrastructure/ui-services/seasonUiService";
import ButtonSelectTypeStats from "~/app/components/pages/region-stats/ButtonSelectTypeStats.vue";

false && /*#__PURE__*/ defineI18nRoute({
  paths: {
    en: "/[region]/stats/teams",
    es: "/[region]/estadisticas/equipos",
    de: "/[region]/statistiken/mannschaften",
    fr: "/[region]/statistiques/equipes",
    it: "/[region]/statistiche/squadre",
    pt: "/[region]/estatisticas/times",
  },
});

definePageMeta({
  middleware: ["not-found-king-of-kings-nations"],
});

const { t } = useI18n();
const route = useRoute();
const localePath = useLocalePath();
const { seasonId, seasonClusterId } = useSeasonFilter();
const { contextSiteTitle } = useWebContext();
const { region } = useCompetitionStore();
const gridStatsKey = ref<number>(0);
const sidebarKey = ref<number>(0);
const loadingSeasonData = ref<boolean>(false);
const errorMessage = ref<boolean>(false);

const getSeasonData = async (): Promise<void> => {
  if ($SEASONS_WITHOUT_DATA.includes(Number(seasonId.value))) {
    await navigateTo(
      localePath({ name: "region-stats", query: { season: seasonClusterId.value, split: seasonId.value } })
    );
  } else {
    try {
      loadingSeasonData.value = true;
      errorMessage.value = false;
    } catch (e) {
      errorMessage.value = true;
    } finally {
      loadingSeasonData.value = false;
      gridStatsKey.value++;
    }
  }
};

await nextTick();
await getSeasonData();

useSeoMeta({
  title: t("header.navCompetition.stats") + " " + t("header.navCompetition.statsTeams"),
  ogTitle: t("header.navCompetition.stats") + " " + t("header.navCompetition.statsTeams"),
  description: t("meta.description.stats.page.teams", { league: region.leagueTitle || contextSiteTitle }),
  ogDescription: t("meta.description.stats.page.teams", { league: region.leagueTitle || contextSiteTitle }),
});
</script>

<template>
  <header-banner-title
    :title="$t('general.stats.title') + ' ' + $t('header.navCompetition.statsTeams')"
  ></header-banner-title>
  <section class="px-4 md:px-6">
    <div
      class="kql-w-container flex flex-col md:flex-row items-stretch sm:items-end justify-center md:items-center md:justify-between flex-wrap"
    >
      <button-select-type-stats data-type="teams" :page="undefined"></button-select-type-stats>
      <seasons-filter
        v-model:seasonClusterId="seasonClusterId"
        v-model:seasonId="seasonId"
        @change="getSeasonData"
        combined-with-more-filters
      />
    </div>
  </section>
  <section class="px-4 md:px-6 py-9">
    <div class="kql-w-container grid grid-cols-1 lg:grid-cols-4 py-9 px-4 bg-gray-900 rounded-lg lg:gap-4">
      <div class="mb-9 lg:mb-0">
        <sidebar-menu-stats
          :new-stats="!$SEASONS_WITHOUT_DATA.includes(Number(seasonId))"
          :key="sidebarKey"
          :season-id="seasonId"
          is-teams
        ></sidebar-menu-stats>
      </div>
      <div class="col-span-3">
        <div v-if="!errorMessage">
          <grid-featured-stats-teams-overview
            :key="gridStatsKey"
            :loading="loadingSeasonData"
            :season-id="seasonId"
          ></grid-featured-stats-teams-overview>
        </div>
        <div v-else>{{ $t("stats.message.notAvailable") }}</div>
      </div>
    </div>
  </section>
</template>

<style scoped></style>
